import { Box, ButtonBase, Typography } from "@mui/material";

import styled, { css, keyframes } from "styled-components";

export const ChartWrapper = styled.div`
    width: 100%;
    position: relative;
    overflow: visible;
`;

/**
 * The plot keeps its own fixed height so a legend that wraps onto several
 * rows grows the card downwards instead of squeezing the chart.
 */
export const ChartArea = styled.div`
    width: 100%;
    height: 400px;
    position: relative;
    overflow: visible;

    .recharts-responsive-container,
    .recharts-wrapper,
    .recharts-surface {
        overflow: visible;
    }
`;

export const EmptyStateContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    min-height: 300px;
    padding: 40px 20px;
    text-align: center;
    background-color: #fafafa;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
`;

export const EmptyStateIconWrapper = styled(Box)`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;

    .MuiSvgIcon-root {
        font-size: 40px;
        color: #999;
    }
`;

export const EmptyStateTitle = styled(Typography).attrs({ variant: "h6" })`
    && {
        color: #666;
        font-weight: 500;
        margin-bottom: 8px;
    }
`;

export const EmptyStateDescription = styled(Typography).attrs({
    variant: "body2",
})`
    && {
        color: #999;
        max-width: 300px;
        line-height: 1.5;
    }
`;

export const LegendList = styled.div`
    width: 100%;
    display: flex;
    gap: 8px 24px;
    align-items: center;
    justify-content: center;
    margin-top: 4px;
    flex-wrap: wrap;
`;

const legendClickPulse = keyframes`
    0% {
        transform: scale(1);
    }
    35% {
        transform: scale(0.94);
    }
    65% {
        transform: scale(1.03);
    }
    100% {
        transform: scale(1);
    }
`;

const squarePop = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(0.75);
    }
    100% {
        transform: scale(1);
    }
`;

export const LegendItem = styled(ButtonBase)`
    && {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        border-radius: 8px;
        padding: 6px 12px;
        cursor: pointer;
        overflow: hidden;
        position: relative;
        opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.55)};
        transition:
            opacity 0.3s ease,
            background-color 0.2s ease,
            box-shadow 0.2s ease,
            transform 0.2s ease;

        ${({ $isAnimating }) =>
            $isAnimating &&
            css`
                animation: ${legendClickPulse} 0.45s
                    cubic-bezier(0.34, 1.56, 0.64, 1);
            `}

        &:hover {
            background-color: rgba(0, 0, 0, 0.05);
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        &:active {
            transform: scale(0.96);
        }

        .MuiTouchRipple-root {
            opacity: 0.35;
        }

        .square {
            width: 16px;
            display: inline-block;
            height: 16px;
            border-radius: 5px;
            background: ${({ $color }) => $color || "red"};
            flex-shrink: 0;
            transition:
                transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
                opacity 0.3s ease,
                filter 0.3s ease,
                box-shadow 0.3s ease;
            filter: ${({ $isVisible }) =>
                $isVisible ? "none" : "grayscale(1)"};
            opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.45)};
            box-shadow: ${({ $isVisible, $color }) =>
                $isVisible ? `0 0 0 0 ${$color || "red"}` : "none"};

            ${({ $isAnimating }) =>
                $isAnimating &&
                css`
                    animation: ${squarePop} 0.45s
                        cubic-bezier(0.34, 1.56, 0.64, 1);
                `}
        }

        .title {
            color: ${({ $isVisible }) => ($isVisible ? "black" : "#999")};
            font-weight: 600;
            text-decoration: ${({ $isVisible }) =>
                $isVisible ? "none" : "line-through"};
            text-decoration-color: #bbb;
            transition:
                color 0.3s ease,
                text-decoration-color 0.3s ease;
        }
    }
`;

const styledComponents = {
    ChartWrapper,
    ChartArea,
    EmptyStateContainer,
    EmptyStateIconWrapper,
    EmptyStateTitle,
    EmptyStateDescription,
    LegendList,
    LegendItem,
};

export default styledComponents;
